import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { updateUpVoteQuestion } from 'features/productQuestionsAndAnswers/slice';
import { getFormatDateWithDaysAgo } from 'utilities/date';
import { CollapsibleContent, CollapsibleDisclosure, useCollapsibleState } from 'components/Collapsible';
import { EditIcon } from 'components/Icons';
import { utagLinkPDP } from 'features/tealium';

import { getDisplayUserName } from '../utility';

import ProductQuestionsAndAnswersUpVote from './ProductQuestionsAndAnswersUpVote';
import ProductQuestionsAndAnswersBestAnswerCard from './ProductQuestionsAndAnswersBestAnswerCard';
import ProductSubmitAnswerForm from './ProductSubmitAnswerForm';
// eslint-disable-next-line max-len
import ProductQuestionsAndAnswersCardCollapsibleMoreAnswers from './ProductQuestionsAndAnswersCardCollapsibleMoreAnswers';
import ProductQuestionsAndAnswersThankYou from './ProductQuestionsAndAnswersThankYou';
import { questionsPropTypes } from './propTypes';

import './ProductQuestionsAndAnswersCard.scss';

const isMobile = !!(window.utag_data && window.utag_data.is_mobile == '1');
const ProductQuestionsAndAnswersCard = ({
	question,
	showAnswers = false,
	showBestAnswer = true,
	styleVariant = '',
	fromSearch = false,
	questionPos,
	pageNumber
}) => {

	const {
		answers, answerCount, dateCreated, id, isStaff, text, upVotes, user
	} = question;

	const dispatch = useDispatch();

	const [ upVoteClicked, setUpVoteClicked ] = useState(false);
	const [ upVoteCount, setUpVoteCount ] = useState(upVotes);
	const collapsibleShowHideAnswers = useCollapsibleState(Boolean(showAnswers));
	const collapsibleBestAnswerCard = useCollapsibleState();
	const collapsibleQuestionListCard = useCollapsibleState();
	// eslint-disable-next-line max-len
	const collapsibleShowHideAnswersAriaControl = `${collapsibleBestAnswerCard.collapsibleContentId} ${collapsibleQuestionListCard.collapsibleContentId}`;

	const collapsibleShowHideCreateAnswer = useCollapsibleState(false);
	const [ showThankYouMessage, setShowThankYouMessage ] = useState(false);

	const openShowHideAnswer = collapsibleShowHideAnswers.open;
	const openCreateAnswer = collapsibleShowHideCreateAnswer.open;

	const toggleAnswersClass = classNames('ProductQuestionsAndAnswersCard__arrow', {
		'ProductQuestionsAndAnswersCard--show': !openShowHideAnswer,
		'ProductQuestionsAndAnswersCard--hide': openShowHideAnswer
	});

	const toggleAnswersContainerClass = classNames('ProductQuestionsAndAnswersCard__answersButtonContainer', {
		'ProductQuestionsAndAnswersCard__answersButtonContainer--show': !openShowHideAnswer || !openCreateAnswer,
		'ProductQuestionsAndAnswersCard__answersButtonContainer--hide': openShowHideAnswer || openCreateAnswer
	});

	const bestAnswerArrayFiltered = answers.filter((answer) => answer.isBestAnswer);

	const handleUpVoteClick = () => {

		dispatch(updateUpVoteQuestion(id));
		setUpVoteClicked(true);
		setUpVoteCount(upVoteCount + 1);

	};

	const handleCancelClick = () => {

		collapsibleShowHideCreateAnswer.setOpen(false);

	};

	const handleShowThankYouMessage = () => {

		setShowThankYouMessage(true);

	};

	return (
		<div
			className={classNames('ProductQuestionsAndAnswersCard', {
				[`ProductQuestionsAndAnswersCard--${styleVariant}`]: Boolean(styleVariant)
			})}
		>
			<div className="ProductQuestionsAndAnswersCard__questionContainer">
				<div className="ProductQuestionsAndAnswersCard__content">
					<div className="ProductQuestionsAndAnswersCard__header">
						<div className="ProductQuestionsAndAnswersCard__question">
							<span className="ProductQuestionsAndAnswersCard__questionMark">
								<strong>Q:</strong>
							</span>
							{text}
							<div className="ProductQuestionsAndAnswersCard__questionUserName">
								{getDisplayUserName(user)}
							</div>
							{isStaff && <span className="ProductQuestionsAndAnswersCard__badge">staff</span>}
							<div className="ProductQuestionsAndAnswersCard__questionDate">
								{getFormatDateWithDaysAgo(dateCreated)}
							</div>
						</div>
					</div>

					{showBestAnswer && bestAnswerArrayFiltered.length > 0 && (
						<ProductQuestionsAndAnswersBestAnswerCard {...bestAnswerArrayFiltered[0]} />
					)}
				</div>
				{!isMobile && (
					<div className="ProductQuestionsAndAnswersCard__upVote">
						<ProductQuestionsAndAnswersUpVote
							disabled={upVoteClicked}
							handleUpVoteClick={handleUpVoteClick}
							upVoteCount={upVoteCount}
						/>
					</div>
				)}
			</div>

			<div className={toggleAnswersContainerClass}>
				{answerCount > 0 && (
					<div className="ProductQuestionsAndAnswersCard__answersShowHideButton">
						<CollapsibleDisclosure
							{...collapsibleShowHideAnswers}
							collapsibleContentId={collapsibleShowHideAnswersAriaControl}
							onClick={(e, isOpen) => {

								if (!fromSearch && pageNumber && questionPos) {

									utagLinkPDP('TurnTo')({
										eventAction: `Q&A-${isOpen ? 'see' : 'hide'}Answers`,
										eventLabel: `${pageNumber}-${questionPos}`
									});

								}

							}}
						>
							<span className={toggleAnswersClass} />
							<span className="ProductQuestionsAndAnswersCard--underline">
								{!openShowHideAnswer ? 'See' : 'Hide'}
								{' '}
								{answerCount}
								{' '}
								{answerCount > 1 ? 'Answers' : 'Answer'}
							</span>
						</CollapsibleDisclosure>
					</div>
				)}
				<div className="ProductQuestionsAndAnswersCard__openCreateAnswerButton">
					<CollapsibleDisclosure
						{...collapsibleShowHideCreateAnswer}
						onClick={(e, isOpen) => {

							if (!fromSearch && pageNumber && questionPos && isOpen) {

								utagLinkPDP('TurnTo')({
									eventAction: 'Q&A-answerLink',
									eventLabel: `${pageNumber}-${questionPos}`
								});

							}

						}}
					>
						<span className="ProductQuestionsAndAnswersCard__editIcon">
							<EditIcon height="10px" width="10px" />
						</span>
						<span className="ProductQuestionsAndAnswersCard--underline">Answer</span>
					</CollapsibleDisclosure>
				</div>
				{isMobile && (
					<div className="ProductQuestionsAndAnswersCard__upVote">
						<ProductQuestionsAndAnswersUpVote
							disabled={upVoteClicked}
							handleUpVoteClick={handleUpVoteClick}
							upVoteCount={upVoteCount}
						/>
					</div>
				)}
			</div>

			<CollapsibleContent {...collapsibleShowHideCreateAnswer} open={openCreateAnswer}>
				<ProductSubmitAnswerForm
					questionId={id}
					onCancel={handleCancelClick}
					handleShowThankYouMessage={handleShowThankYouMessage}
					questionPos={questionPos}
					pageNumber={pageNumber}
					fromSearch={fromSearch}
				/>
			</CollapsibleContent>

			{showThankYouMessage && (
				<div className="ProductQuestionsAndAnswersCard__answerThankYouMessage">
					<ProductQuestionsAndAnswersThankYou message="Your answer has been submitted. If you provided your email, you may receive notifications for 14 days. Otherwise, return to this page to see any replies." />
				</div>
			)}
			{answerCount > 0 && (
				<ProductQuestionsAndAnswersCardCollapsibleMoreAnswers
					answers={answers}
					{...collapsibleQuestionListCard}
					open={openShowHideAnswer}
					collapsibleDisclosureId={collapsibleShowHideAnswers.collapsibleDisclosureId}
					fromSearch={fromSearch}
					pageNumber={pageNumber}
					questionPos={questionPos}
				/>
			)}
		</div>
	);

};
ProductQuestionsAndAnswersCard.propTypes = {
	question: questionsPropTypes,
	showAnswers: PropTypes.bool,
	showBestAnswer: PropTypes.bool,
	styleVariant: PropTypes.string,
	fromSearch: PropTypes.bool,
	questionPos: PropTypes.number,
	pageNumber: PropTypes.number
};
export default ProductQuestionsAndAnswersCard;
